import React, { useState, useEffect } from 'react';
import {
  FiHome, FiUsers, FiFileText, FiPhone, FiMail, FiMapPin,
  FiSearch, FiFilter, FiCheck, FiX, FiCheckCircle, FiXCircle,
  FiEye, FiEdit3, FiSmartphone, FiDollarSign, FiRefreshCw
} from 'react-icons/fi';
import { superAdminAPI } from '../../services/api';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';

const AgenciesManagementPage = () => {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [feedback, setFeedback] = useState(null);

  // Modal Détails Agence & Locataires
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [agencyDetails, setAgencyDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailTab, setDetailTab] = useState('tenants'); // 'tenants' | 'properties' | 'sms' | 'users'

  // Modal Ajustement SMS
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('Attribution manuelle Super Admin');
  const [adjusting, setAdjusting] = useState(false);

  const fetchAgencies = async () => {
    setLoading(true);
    try {
      const res = await superAdminAPI.getAgencies({ search, status: statusFilter });
      setAgencies(res.data || []);
    } catch (error) {
      console.error('Erreur chargement agences:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgencies();
  }, [statusFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchAgencies();
  };

  const handleOpenDetails = async (agency) => {
    setSelectedAgencyId(agency.id);
    setLoadingDetails(true);
    setDetailTab('tenants');
    try {
      const res = await superAdminAPI.getAgencyDetails(agency.id);
      setAgencyDetails(res.data);
    } catch (err) {
      alert('Erreur lors du chargement des détails de l\'agence');
      setSelectedAgencyId(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleToggleStatus = async (agency) => {
    const newStatus = !agency.is_active;
    if (!window.confirm(`Voulez-vous vraiment ${newStatus ? 'activer' : 'suspendre'} l'agence "${agency.name}" ?`)) return;

    try {
      await superAdminAPI.toggleAgencyStatus(agency.id, { isActive: newStatus });
      setFeedback({
        type: 'success',
        text: `Agence "${agency.name}" ${newStatus ? 'activée' : 'suspendue'} avec succès.`
      });
      fetchAgencies();
    } catch (err) {
      setFeedback({
        type: 'error',
        text: 'Erreur lors de la modification du statut de l\'agence.'
      });
    }
  };

  const handleAdjustSms = async (e) => {
    e.preventDefault();
    if (!adjustModal) return;
    setAdjusting(true);
    try {
      await superAdminAPI.adjustAgencySms(adjustModal.id, {
        amount: parseInt(adjustAmount, 10),
        reason: adjustReason,
      });
      setFeedback({
        type: 'success',
        text: `Crédits SMS mis à jour pour ${adjustModal.name} !`
      });
      setAdjustModal(null);
      setAdjustAmount('');
      fetchAgencies();
      if (selectedAgencyId === adjustModal.id) {
        handleOpenDetails(adjustModal);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'attribution');
    } finally {
      setAdjusting(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  const columns = [
    {
      header: 'Agence',
      render: r => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 8, background: '#eff6ff',
            color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 14
          }}>
            {r.name?.substring(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{r.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {r.city || 'Mali'} {r.address ? `• ${r.address}` : ''}
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Contact',
      render: r => (
        <div style={{ fontSize: 12 }}>
          <div>📞 {r.phone || 'Non renseigné'}</div>
          <div style={{ color: 'var(--text-secondary)' }}>✉️ {r.email || '—'}</div>
        </div>
      )
    },
    {
      header: 'Parc Géré',
      render: r => (
        <div style={{ fontSize: 12 }}>
          <div>🏠 <strong>{r.properties_count}</strong> bien(s)</div>
          <div>👤 <strong>{r.tenants_count}</strong> locataire(s)</div>
          <div style={{ color: '#16a34a' }}>📄 <strong>{r.active_leases_count}</strong> contrat(s) actif(s)</div>
        </div>
      )
    },
    {
      header: 'Solde SMS BatakiPlus',
      render: r => (
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: (r.sms_credits || 0) > 0 ? '#7c3aed' : '#ef4444' }}>
            🪙 {fmt(r.sms_credits)} SMS
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            Total envoyé: {fmt(r.total_sms_sent)}
          </div>
        </div>
      )
    },
    {
      header: 'Statut',
      render: r => (
        <span className={`badge badge-${r.is_active ? 'success' : 'danger'}`}>
          {r.is_active ? 'Active' : 'Suspendue'}
        </span>
      )
    },
    {
      header: 'Actions',
      render: r => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => handleOpenDetails(r)}
            title="Inspecter l'agence et voir tous ses locataires"
          >
            <FiEye style={{ marginRight: 4 }} /> Détails & Locataires
          </button>
          <button
            className="btn btn-sm"
            style={{ background: '#7c3aed', color: '#fff' }}
            onClick={() => { setAdjustModal(r); setAdjustAmount('50'); }}
            title="Attribuer ou ajuster les crédits SMS"
          >
            🪙 SMS
          </button>
          <button
            className={`btn btn-sm btn-${r.is_active ? 'secondary' : 'success'}`}
            onClick={() => handleToggleStatus(r)}
            title={r.is_active ? 'Suspendre cette agence' : 'Activer cette agence'}
          >
            {r.is_active ? <FiX /> : <FiCheck />}
          </button>
        </div>
      )
    }
  ];

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>Gestion des Agences & de leurs Locataires</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Supervisez les agences inscrites, explorez leurs locataires et gérez les attributions de crédits SMS
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={fetchAgencies}>
            <FiRefreshCw style={{ marginRight: 4 }} /> Actualiser
          </button>
        </div>
      </div>

      {/* Feedback message */}
      {feedback && (
        <div className="card" style={{
          marginBottom: 20,
          background: feedback.type === 'success' ? '#dcfce7' : '#fee2e2',
          borderLeft: `5px solid ${feedback.type === 'success' ? '#22c55e' : '#ef4444'}`,
          padding: 14
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: feedback.type === 'success' ? '#15803d' : '#b91c1c' }}>
              {feedback.text}
            </span>
            <button className="btn btn-sm btn-secondary" onClick={() => setFeedback(null)}>OK</button>
          </div>
        </div>
      )}

      {/* Barre de filtres */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ padding: 16 }}>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 260, position: 'relative' }}>
              <FiSearch style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-secondary)' }} />
              <input
                type="text"
                className="form-control"
                style={{ paddingLeft: 36 }}
                placeholder="Rechercher par nom d'agence, email, téléphone, ville..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              className="form-control"
              style={{ width: 180 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              <option value="active">Agences Actives</option>
              <option value="inactive">Agences Suspendues</option>
            </select>

            <button type="submit" className="btn btn-primary">
              Filtrer
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setSearch(''); setStatusFilter(''); }}
            >
              Réinitialiser
            </button>
          </form>
        </div>
      </div>

      {/* Tableau des agences */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Liste des Agences Immobilières ({agencies.length})</h2>
        </div>
        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <DataTable columns={columns} data={agencies} emptyMessage="Aucune agence trouvée avec ces critères." />
        )}
      </div>

      {/* MODAL DÉTAILS DE L'AGENCE & SES LOCATAIRES */}
      {selectedAgencyId && (
        <Modal
          isOpen={true}
          onClose={() => { setSelectedAgencyId(null); setAgencyDetails(null); }}
          title={`🏢 Agence : ${agencyDetails?.agency?.name || 'Chargement...'}`}
          maxWidth="950px"
        >
          {loadingDetails || !agencyDetails ? (
            <div className="loading" style={{ padding: 40 }}><div className="spinner" /></div>
          ) : (
            <div>
              {/* En-tête agence */}
              <div style={{ background: '#f8fafc', padding: 16, borderRadius: 10, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18 }}>{agencyDetails.agency.name}</h3>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                    📞 {agencyDetails.agency.phone || 'Non renseigné'} • ✉️ {agencyDetails.agency.email} • 📍 {agencyDetails.agency.city || 'Mali'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{ background: '#faf5ff', border: '1px solid #ddd6fe', padding: '8px 14px', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, color: '#6d28d9', fontWeight: 600 }}>SOLDE CRÉDITS SMS</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#7c3aed' }}>🪙 {agencyDetails.agency.sms_credits || 0} SMS</div>
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{ background: '#7c3aed', color: '#fff' }}
                    onClick={() => { setAdjustModal(agencyDetails.agency); setAdjustAmount('50'); }}
                  >
                    + Ajuster SMS
                  </button>
                </div>
              </div>

              {/* Onglets de navigation */}
              <div className="tabs" style={{ marginBottom: 16 }}>
                {[
                  { key: 'tenants', label: `👤 Locataires (${agencyDetails.tenants.length})` },
                  { key: 'properties', label: `🏠 Biens (${agencyDetails.properties.length})` },
                  { key: 'users', label: `👥 Agents (${agencyDetails.users.length})` },
                  { key: 'sms', label: `📱 Commandes & Logs SMS (${agencyDetails.smsOrders.length})` },
                ].map(t => (
                  <button
                    key={t.key}
                    className={`tab ${detailTab === t.key ? 'active' : ''}`}
                    onClick={() => setDetailTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* CONTENU ONGLET 1 : LOCATAIRES DE L'AGENCE */}
              {detailTab === 'tenants' && (
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {agencyDetails.tenants.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 20 }}>Cette agence n'a pas encore enregistré de locataires.</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Nom & Prénom</th>
                          <th>Contact</th>
                          <th>Logement Actuel</th>
                          <th>Contrats</th>
                          <th>Inscrit le</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agencyDetails.tenants.map(t => (
                          <tr key={t.id}>
                            <td>
                              <strong>{t.first_name} {t.last_name}</strong>
                              {t.profession && <div style={{ fontSize: 11, color: '#64748b' }}>{t.profession}</div>}
                            </td>
                            <td>
                              <div>📞 {t.phone || '—'}</div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>✉️ {t.email || '—'}</div>
                            </td>
                            <td>
                              {t.current_property ? (
                                <span style={{ fontWeight: 600, color: '#2563eb' }}>🏠 {t.current_property}</span>
                              ) : (
                                <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Aucun bail actif</span>
                              )}
                            </td>
                            <td>
                              <span className="badge badge-secondary">{t.contracts_count || 0} contrat(s)</span>
                            </td>
                            <td style={{ fontSize: 12 }}>{fmtDate(t.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* CONTENU ONGLET 2 : BIENS IMMOBILIERS */}
              {detailTab === 'properties' && (
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Titre du Bien</th>
                        <th>Type & Emplacement</th>
                        <th>Prix Loyer / Vente</th>
                        <th>Disponibilité</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencyDetails.properties.map(p => (
                        <tr key={p.id}>
                          <td><strong>{p.title}</strong></td>
                          <td>{p.property_type || 'Bien'} • {p.neighborhood ? `${p.neighborhood}, ` : ''}{p.city}</td>
                          <td><strong>{fmt(p.price)} XOF</strong></td>
                          <td>
                            <span className={`badge badge-${p.is_available ? 'success' : 'warning'}`}>
                              {p.is_available ? 'Disponible' : 'Occupé'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* CONTENU ONGLET 3 : UTILISATEURS */}
              {detailTab === 'users' && (
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Email & Téléphone</th>
                        <th>Rôle</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencyDetails.users.map(u => (
                        <tr key={u.id}>
                          <td><strong>{u.first_name} {u.last_name}</strong></td>
                          <td>{u.email} {u.phone ? `• ${u.phone}` : ''}</td>
                          <td><span className="badge badge-info">{u.role_name || 'Agent'}</span></td>
                          <td><span className={`badge badge-${u.is_active ? 'success' : 'danger'}`}>{u.is_active ? 'Actif' : 'Inactif'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* CONTENU ONGLET 4 : COMMANDES & LOGS SMS */}
              {detailTab === 'sms' && (
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>Commandes de packs SMS</h4>
                  <table className="data-table" style={{ marginBottom: 20 }}>
                    <thead>
                      <tr>
                        <th>SMS</th>
                        <th>Prix</th>
                        <th>Méthode</th>
                        <th>Référence</th>
                        <th>Statut</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencyDetails.smsOrders.map(o => (
                        <tr key={o.id}>
                          <td><strong>🪙 {o.sms_count} SMS</strong></td>
                          <td>{fmt(o.price)} XOF</td>
                          <td>{o.payment_method}</td>
                          <td><code>{o.payment_reference}</code></td>
                          <td><span className={`badge badge-${o.status === 'approved' ? 'success' : (o.status === 'pending' ? 'warning' : 'danger')}`}>{o.status}</span></td>
                          <td style={{ fontSize: 11 }}>{fmtDate(o.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button className="btn btn-secondary" onClick={() => { setSelectedAgencyId(null); setAgencyDetails(null); }}>
                  Fermer
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* MODAL AJUSTEMENT SMS */}
      {adjustModal && (
        <Modal isOpen={true} onClose={() => setAdjustModal(null)} title={`🪙 Ajuster les crédits SMS : ${adjustModal.name}`}>
          <form onSubmit={handleAdjustSms}>
            <div style={{ background: '#f8fafc', padding: 14, borderRadius: 8, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{adjustModal.name}</strong>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{adjustModal.phone || adjustModal.email}</div>
                </div>
                <div>
                  <span style={{ fontSize: 12, color: '#64748b' }}>Solde actuel : </span>
                  <strong style={{ fontSize: 16, color: '#2563eb' }}>{adjustModal.sms_credits || 0} SMS</strong>
                </div>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                Nombre de SMS à créditer (ex: 50, 100, ou -20 pour déduire) *
              </label>
              <input
                type="number"
                className="form-control"
                required
                placeholder="Ex: 50"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, display: 'block' }}>
                Motif de l'opération *
              </label>
              <input
                type="text"
                className="form-control"
                required
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setAdjustModal(null)} disabled={adjusting}>
                Annuler
              </button>
              <button type="submit" className="btn btn-primary" disabled={adjusting}>
                {adjusting ? 'Traitement...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default AgenciesManagementPage;
